import { useState, useMemo, useCallback, memo, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { formatPhoneNumber } from "@/lib/utils";
import { formatCurrency } from "@/utils";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { DollarSign, User, Phone, ChevronDown, PhoneCall, Video, Calendar, Loader2, ListChecks, AlertCircle } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { format, isToday, isTomorrow } from "date-fns";
import { ptBR, enUS } from "date-fns/locale";
import { supabase } from "@/integrations/supabase/client";
import { LostReasonModal } from "@/components/deals/LostReasonModal";
import { PostMeetingModal } from "@/components/deals/PostMeetingModal";
import { QualificationRequiredModal } from "@/components/deals/QualificationRequiredModal";
import { validateDealForScheduled } from "@/hooks/useStageTransition";
import { InlineDealTagsDisplay } from "@/components/deals/DealTagsDisplay";

const stageConfig = {
  new: { 
    bgColor: "bg-slate-50 dark:bg-slate-800/50", 
    borderColor: "border-slate-100 dark:border-slate-700",
    countBg: "bg-slate-100 dark:bg-slate-700",
    countText: "text-slate-700 dark:text-slate-300"
  },
  contact: { 
    bgColor: "bg-blue-50 dark:bg-blue-900/30", 
    borderColor: "border-blue-100 dark:border-blue-800",
    countBg: "bg-blue-100 dark:bg-blue-900",
    countText: "text-blue-700 dark:text-blue-300"
  },
  scheduled: { 
    bgColor: "bg-purple-50 dark:bg-purple-900/30", 
    borderColor: "border-purple-100 dark:border-purple-800",
    countBg: "bg-purple-100 dark:bg-purple-900",
    countText: "text-purple-700 dark:text-purple-300"
  },
  proposal: { 
    bgColor: "bg-amber-50 dark:bg-amber-900/30", 
    borderColor: "border-amber-100 dark:border-amber-800",
    countBg: "bg-amber-100 dark:bg-amber-900",
    countText: "text-amber-700 dark:text-amber-300"
  },
  negotiation: { 
    bgColor: "bg-orange-50 dark:bg-orange-900/30", 
    borderColor: "border-orange-100 dark:border-orange-800",
    countBg: "bg-orange-100 dark:bg-orange-900",
    countText: "text-orange-700 dark:text-orange-300"
  },
  won: { 
    bgColor: "bg-emerald-50 dark:bg-emerald-900/30", 
    borderColor: "border-emerald-100 dark:border-emerald-800",
    countBg: "bg-emerald-100 dark:bg-emerald-900",
    countText: "text-emerald-700 dark:text-emerald-300"
  },
  lost: { 
    bgColor: "bg-rose-50 dark:bg-rose-900/30", 
    borderColor: "border-rose-100 dark:border-rose-800",
    countBg: "bg-rose-100 dark:bg-rose-900",
    countText: "text-rose-700 dark:text-rose-300"
  }
};

// Helper to format scheduled call date
const formatScheduledDate = (dateStr, t, locale) => {
  const date = new Date(dateStr);
  const time = format(date, "HH:mm");
  if (isToday(date)) {
    return t('dates.todayAt', { time });
  }
  if (isTomorrow(date)) {
    return t('dates.tomorrowAt', { time });
  }
  return format(date, "EEE dd/MM", { locale }) + ` ${t('dates.at')} ${time}`;
};

// Memoized Deal Card Component
const DealCard = memo(({ 
  deal, 
  index, 
  getContactName, 
  getContactPhone, 
  getAssignedUserName, 
  onViewDeal,
  isSelected,
  onToggleSelect,
  scheduledCall,
  taskCount,
  taskTitles,
  t,
  dateLocale
}) => {
  const contactPhone = getContactPhone(deal);
  
  return (
    <Draggable key={deal.id} draggableId={deal.id} index={index}>
      {(provided, snapshot) => (
        <Link 
          to={`/deal-detail?id=${deal.id}`}
          onClick={(e) => {
            // Se está em modo seleção, qualquer clique seleciona
            if (onToggleSelect) {
              e.preventDefault();
              e.stopPropagation();
              onToggleSelect(deal.id);
              return;
            }
            
            // Se não for Ctrl/Cmd+Click, previne navegação padrão e usa SPA
            if (!e.ctrlKey && !e.metaKey) {
              e.preventDefault();
              if (!snapshot.isDragging) {
                onViewDeal(deal.id);
              }
            }
            // Se for Ctrl/Cmd+Click, deixa o Link fazer navegação em nova aba
          }}
          className="block"
        >
          <Card
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            className={`bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 cursor-pointer hover:shadow-md transition-shadow ${
              isSelected ? "ring-2 ring-primary border-primary" : ""
            } ${snapshot.isDragging ? "shadow-xl rotate-2" : ""}`}
          >
            <CardContent className="p-4 space-y-3">
              {/* Checkbox */}
              {onToggleSelect && (
                <div className="flex justify-end checkbox-wrapper">
                  <Checkbox
                    checked={isSelected}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onToggleSelect(deal.id);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.stopPropagation();
                        onToggleSelect(deal.id);
                      }
                    }}
                    aria-label="Select deal"
                  />
                </div>
              )}
              {/* Deal Name */}
              <h4 className="text-sm font-medium text-slate-900 dark:text-slate-100 leading-tight">
                {deal.name || deal.deal_name || getContactName(deal)}
              </h4>

              {/* Contact Name */}
              <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                <User className="w-4 h-4 flex-shrink-0" />
                <span className="text-sm">{getContactName(deal)}</span>
              </div>

              {/* Contact Phone */}
              {contactPhone.phone && (
                <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                  <Phone className="w-4 h-4 flex-shrink-0" />
                  <span className="text-sm">{formatPhoneNumber(contactPhone.phone, contactPhone.countryCode)}</span>
                </div>
              )}

              {/* Deal Value */}
              {(deal.total_value ?? deal.total_amount ?? 0) > 0 && (
                <div className="flex items-center gap-2 pt-2 border-t border-slate-100 dark:border-slate-700">
                  <DollarSign className="w-4 h-4 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                  <span className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">
                    ${(deal.total_value ?? deal.total_amount ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </span>
                </div>
              )}

              {/* Scheduled Call Indicator */}
              {scheduledCall && (
                <div className={`flex items-center gap-2 px-2 py-1.5 rounded-md ${
                  isToday(new Date(scheduledCall.scheduled_for)) 
                    ? 'bg-emerald-50 text-emerald-700' 
                    : 'bg-blue-50 text-blue-700'
                }`}>
                  {scheduledCall.channel === 'video' ? (
                    <Video className="w-3.5 h-3.5 flex-shrink-0" />
                  ) : (
                    <PhoneCall className="w-3.5 h-3.5 flex-shrink-0" />
                  )}
                  <span className="text-xs font-medium">
                    {formatScheduledDate(scheduledCall.scheduled_for, t, dateLocale)}
                  </span>
                </div>
              )}

              {/* Task Indicator */}
              {taskCount > 0 && (() => {
                const firstTask = taskTitles?.[0];
                const taskTitle = typeof firstTask === 'string' ? firstTask : firstTask?.title;
                const taskDueDate = typeof firstTask === 'string' ? null : firstTask?.due_date;
                const taskDueTime = typeof firstTask === 'string' ? null : firstTask?.due_time;
                // Parse date-only field as local time to avoid UTC-shift bug
                const taskDueLocal = taskDueDate
                  ? new Date(`${taskDueDate}T${taskDueTime || '23:59:00'}`)
                  : null;
                const isOverdue = taskDueLocal && taskDueLocal < new Date();
                return (
                  <div className={`flex flex-col gap-0.5 text-xs px-2 py-1.5 rounded-md ${
                    isOverdue
                      ? 'bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-400'
                      : 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                  }`}>
                    <div className="flex items-center gap-1.5">
                      <ListChecks className="w-3.5 h-3.5 flex-shrink-0" />
                      <span className="font-medium truncate">{taskTitle?.length > 40 ? taskTitle.slice(0, 40) + '…' : taskTitle}</span>
                    </div>
                    {taskDueLocal && (
                      <div className="flex items-center gap-1.5 ml-5">
                        {isOverdue ? (
                          <AlertCircle className="w-3 h-3 flex-shrink-0" />
                        ) : (
                          <Calendar className="w-3 h-3 flex-shrink-0" />
                        )}
                        <span className="text-[11px] font-medium">
                          {formatScheduledDate(taskDueLocal, t, dateLocale)}
                        </span>
                      </div>
                    )}
                    {taskCount > 1 && (
                      <span className="text-[11px] ml-5 opacity-75">+{taskCount - 1} {taskCount - 1 === 1 ? 'tarefa' : 'tarefas'}</span>
                    )}
                  </div>
                );
              })()}

              {/* Deal Tags */}
              {deal.deal_tags && deal.deal_tags.length > 0 && (
                <InlineDealTagsDisplay 
                  tags={deal.deal_tags} 
                  compact 
                  maxTags={2} 
                />
              )}

              {/* Assigned User */}
              {((deal.sold_by?.display_name || deal.sales_dev?.display_name) || 
                (deal.sales_consultant || deal.sales_development)) && (
                <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
                  <User className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="text-xs truncate">
                    {deal.sold_by?.display_name || 
                     deal.sales_dev?.display_name || 
                     (getAssignedUserName && getAssignedUserName(deal.sales_consultant || deal.sales_development)) ||
                     t('common.unassigned')}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>
        </Link>
      )}
    </Draggable>
  );
});

DealCard.displayName = 'DealCard';

export default function DealKanban({ 
  deals, 
  contacts, 
  isLoading, 
  onStageChange, 
  onViewDeal, 
  getAssignedUserName, 
  kanbanColumnsFilter = "all",
  selectedDealIds = [],
  onToggleSelect,
  scheduledCallsByDeal,
  // New props for per-stage pagination
  dealsByStage = {},
  stageCounts = {},
  hasMoreByStage = {},
  loadingMoreStage = null,
  onLoadMoreForStage,
  // Legacy props
  loadingMore = false,
  stageServerCounts = {},
  // Flag for client-side filters
  hasActiveFilters = false,
  // Flag for global search mode
  isSearching = false,
  // Filtered stage counts (for accurate totals when filters are active)
  filteredStageCounts = {}
}) {
  const { toast } = useToast();
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === 'pt-BR' ? ptBR : enUS;

  // Query task counts per deal (pending tasks only)
  const { data: taskCountsMap = {} } = useQuery({
    queryKey: ["deal-task-counts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tasks")
        .select("deal_id, title, due_date, due_time")
        .not("deal_id", "is", null)
        .neq("status", "completed")
        .order("due_date", { ascending: true, nullsFirst: false });
      if (error) throw error;
      const map = {};
      (data || []).forEach(row => {
        if (!map[row.deal_id]) map[row.deal_id] = { count: 0, titles: [] };
        map[row.deal_id].count++;
        map[row.deal_id].titles.push({ title: row.title, due_date: row.due_date, due_time: row.due_time });
      });
      return map;
    },
    staleTime: 30000,
  });
  
  // States for stage transition modals
  const [lostReasonModal, setLostReasonModal] = useState({ 
    isOpen: false, 
    dealId: null, 
    deal: null 
  });
  const [postMeetingModal, setPostMeetingModal] = useState({ 
    isOpen: false, 
    dealId: null, 
    deal: null 
  });
  const [qualificationModal, setQualificationModal] = useState({ 
    isOpen: false, 
    dealId: null, 
    contactId: null, 
    deal: null,
    missingFields: [] 
  });
  const [isProcessingTransition, setIsProcessingTransition] = useState(false);
  
  // Refs for infinite scroll sentinels
  const scrollSentinelsRef = useRef({});
  const columnRefs = useRef({});

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observers = [];
    
    Object.keys(stageConfig).forEach((stageId) => {
      const sentinel = scrollSentinelsRef.current[stageId];
      const column = columnRefs.current[stageId];
      if (!sentinel || !column) return;
      
      const observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          // Only trigger if column actually has rendered deals (prevents loops from client-side filter mismatch)
          const stageDeals = dealsByStage[stageId] || [];
          if (entry.isIntersecting && hasMoreByStage[stageId] && !loadingMoreStage && onLoadMoreForStage && stageDeals.length > 0) {
            console.log(`🔄 [Kanban] Infinite scroll triggered for ${stageId}`);
            onLoadMoreForStage(stageId);
          }
        },
        { 
          threshold: 0.1, 
          root: column 
        }
      );
      
      observer.observe(sentinel);
      observers.push(observer);

      // Manual check: if sentinel is already visible after previous load, trigger immediately
      const stageDeals = dealsByStage[stageId] || [];
      if (hasMoreByStage[stageId] && !loadingMoreStage && onLoadMoreForStage && stageDeals.length > 0) {
        const columnRect = column.getBoundingClientRect();
        const sentinelRect = sentinel.getBoundingClientRect();
        if (sentinelRect.top < columnRect.bottom + 50) {
          onLoadMoreForStage(stageId);
        }
      }
    });
    
    return () => observers.forEach(obs => obs.disconnect());
  }, [hasMoreByStage, loadingMoreStage, onLoadMoreForStage, dealsByStage]);

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    // Disable drag when items are selected
    if (selectedDealIds.length > 0) {
      toast({
        title: t('deals.dragDisabled'),
        description: t('deals.dragDisabledDesc'),
        variant: "destructive",
      });
      return;
    }

    const dealId = result.draggableId;
    const newStage = result.destination.droppableId;
    const fromStage = result.source.droppableId;
    
    // Find deal in dealsByStage or flat deals
    const deal = Object.values(dealsByStage).flat().find(d => d.id === dealId) || 
                 deals.find(d => d.id === dealId);

    if (!deal || deal.stage === newStage) return;

    // Block won - must close from deal detail
    if (newStage === 'won') {
      toast({
        title: t('deals.actionNotAllowed'),
        description: t('deals.closeFromDetails'),
        variant: "destructive",
      });
      return;
    }

    // Lost - open modal for reason
    if (newStage === 'lost') {
      setLostReasonModal({ isOpen: true, dealId, deal });
      return;
    }

    // scheduled -> proposal - open post-meeting modal
    if (fromStage === 'scheduled' && newStage === 'proposal') {
      setPostMeetingModal({ isOpen: true, dealId, deal });
      return;
    }

    // contact -> scheduled - validate qualification
    if (fromStage === 'contact' && newStage === 'scheduled') {
      const validation = await validateDealForScheduled(dealId);
      if (!validation.valid) {
        setQualificationModal({ 
          isOpen: true, 
          dealId, 
          contactId: deal.contact_id,
          deal,
          missingFields: validation.missingFields 
        });
        return;
      }
    }

    // Default: move directly
    onStageChange(deal, newStage);
  };

  // Handler for confirming lost reason from modal (updated signature: reason, lostDate)
  const handleConfirmLostReason = async (reason, lostDate) => {
    const { dealId, deal } = lostReasonModal;
    setIsProcessingTransition(true);
    try {
      // Save to both lost_reason and detailed_lost_reason for compatibility
      await supabase
        .from('deals')
        .update({
          stage: 'lost',
          closed_date: format(lostDate, 'yyyy-MM-dd'),
          lost_reason: reason,
          detailed_lost_reason: reason,
        })
        .eq('id', dealId);
      
      // Trigger refresh via onStageChange callback with skipUpdate flag
      if (onStageChange) {
        onStageChange(deal, 'lost', { skipUpdate: true });
      }
      toast({ title: t('deals.markedAsLost') || 'Deal marcado como perdido' });
    } catch (error) {
      toast({ title: t('common.error'), description: error.message, variant: "destructive" });
    } finally {
      setIsProcessingTransition(false);
      setLostReasonModal({ isOpen: false, dealId: null, deal: null });
    }
  };

  // Handler for post-meeting modal confirmation
  const handleConfirmPostMeeting = async (data) => {
    const { dealId, deal } = postMeetingModal;
    setIsProcessingTransition(true);
    try {
      if (!data.meetingHappened) {
        // No-show: increment counter, DON'T move
        await supabase
          .from('deals')
          .update({
            noshow_count: (deal.noshow_count || 0) + 1,
            meeting_notes: data.noShowReason,
          })
          .eq('id', dealId);
        
        toast({ 
          title: t('stageTransition.noShowRegistered') || 'No-show registrado', 
          description: t('stageTransition.dealRemainsInScheduled') || 'Deal permanece em Scheduled'
        });
      } else {
        // Meeting happened: update and move
        await supabase
          .from('deals')
          .update({
            stage: 'proposal',
            meeting_notes: data.meetingNotes,
            proposal_value: data.proposalValue,
            recommended_process: data.recommendedProcess,
          })
          .eq('id', dealId);
        
        if (onStageChange) {
          onStageChange(deal, 'proposal', { skipUpdate: true });
        }
        toast({ title: t('deals.movedToProposal') || 'Deal movido para Proposta' });
      }
    } catch (error) {
      toast({ title: t('common.error'), description: error.message, variant: "destructive" });
    } finally {
      setIsProcessingTransition(false);
      setPostMeetingModal({ isOpen: false, dealId: null, deal: null });
    }
  };

  // Handler for qualification modal confirmation
  const handleConfirmQualification = async (data) => {
    const { dealId, deal, contactId } = qualificationModal;
    setIsProcessingTransition(true);
    try {
      // Upsert qualification in deal_qualification table
      await supabase
        .from('deal_qualification')
        .upsert({
          deal_id: dealId,
          urgency: data.urgency,
          objective: data.objective,
        }, { onConflict: 'deal_id' });
      
      // Create scheduled meeting if date provided
      if (data.meetingDate) {
        await supabase
          .from('calls')
          .insert({
            deal_id: dealId,
            contact_id: contactId,
            channel: 'video',
            direction: 'outbound',
            scheduled_for: data.meetingDate.toISOString(),
            status: 'scheduled',
          });
      }
      
      // Move to scheduled
      await supabase
        .from('deals')
        .update({ stage: 'scheduled' })
        .eq('id', dealId);
      
      if (onStageChange) {
        onStageChange(deal, 'scheduled', { skipUpdate: true });
      }
      toast({ title: t('stageTransition.meetingScheduled') || 'Reunião agendada' });
    } catch (error) {
      toast({ title: t('common.error'), description: error.message, variant: "destructive" });
    } finally {
      setIsProcessingTransition(false);
      setQualificationModal({ isOpen: false, dealId: null, contactId: null, deal: null, missingFields: [] });
    }
  };

  const getContactName = (deal) => {
    if (deal.contact) {
      return `${deal.contact.first_name} ${deal.contact.last_name}`;
    }
    return t('common.unknown');
  };

  const getContactPhone = (deal) => {
    return {
      phone: deal.contact?.primary_phone || '',
      countryCode: deal.contact?.primary_phone_country_code || ''
    };
  };

  // Get deals for a stage - when filters or search are active, use deals (already filtered)
  // Otherwise prefer dealsByStage for infinite scroll optimization
  const getDealsForStage = useCallback((stageId) => {
    // When client-side filters or global search are active, use the filtered deals prop
    if (hasActiveFilters || isSearching) {
      return deals.filter(deal => deal.stage === stageId);
    }
    
    // No filters: use dealsByStage for optimized infinite scroll
    if (dealsByStage[stageId] && dealsByStage[stageId].length > 0) {
      return dealsByStage[stageId];
    }
    
    return deals.filter(deal => deal.stage === stageId);
  }, [hasActiveFilters, isSearching, dealsByStage, deals]);

  const handleSelectAllInColumn = useCallback((stageId) => {
    const columnDeals = getDealsForStage(stageId);
    const columnDealIds = columnDeals.map(deal => deal.id);
    const allSelected = columnDealIds.every(id => selectedDealIds.includes(id));
    
    if (allSelected) {
      // Desselecionar todos desta coluna
      columnDealIds.forEach(id => {
        if (selectedDealIds.includes(id)) {
          onToggleSelect(id);
        }
      });
    } else {
      // Selecionar todos desta coluna
      columnDealIds.forEach(id => {
        if (!selectedDealIds.includes(id)) {
          onToggleSelect(id);
        }
      });
    }
  }, [getDealsForStage, selectedDealIds, onToggleSelect]);

  // Filter stages based on kanbanColumnsFilter
  const getFilteredStages = () => {
    const naoAtivas = ['won', 'lost'];
    const ativas = Object.keys(stageConfig).filter(stage => !naoAtivas.includes(stage));
    
    if (kanbanColumnsFilter === "all") {
      return Object.keys(stageConfig);
    }
    if (kanbanColumnsFilter === "nao_ativas") {
      return naoAtivas;
    }
    return ativas;
  };

  const filteredStages = getFilteredStages();

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {filteredStages.map((stage) => (
          <div key={stage} className="space-y-3">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className="flex gap-3 overflow-x-auto pb-4 h-[calc(100dvh-220px)] md:h-[calc(100dvh-140px)]">
        {Object.entries(stageConfig)
          .filter(([stageId]) => filteredStages.includes(stageId))
          .map(([stageId, config]) => {
          const stageDeals = getDealsForStage(stageId);
          
          // Get real count:
          // - When filters active: use filteredStageCounts from server RPC (accurate total)
          // - Otherwise: use stageCounts from regular RPC
          // Fallback to stageDeals.length if RPC not yet loaded
          const realCount = hasActiveFilters 
            ? (filteredStageCounts[stageId]?.count ?? stageDeals.length)
            : (stageCounts[stageId]?.count ?? stageServerCounts[stageId] ?? stageDeals.length);
          
          // Get real amount:
          // - When filters active: use filteredStageCounts from server RPC (accurate total)
          // - Otherwise: use stageCounts from regular RPC
          // Fallback to local sum if RPC not yet loaded
          const realAmount = hasActiveFilters
            ? (filteredStageCounts[stageId]?.amount ?? stageDeals.reduce((sum, d) => sum + (Number(d.total_amount) || 0), 0))
            : (stageCounts[stageId]?.amount ?? stageDeals.reduce((sum, d) => sum + (Number(d.total_amount) || 0), 0));
          
          // Check if there are more deals to load
          const hasMore = hasMoreByStage[stageId] ?? false;

          return (
            <div key={stageId} className="flex flex-col flex-shrink-0 w-[280px] min-w-[260px] max-w-[320px] h-full">
              {/* Column Header */}
              <div className={`${config.bgColor} ${config.borderColor} border rounded-t-lg px-4 py-3 flex flex-col gap-1 flex-shrink-0`}>
                {/* Linha 1: Nome + Badge */}
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {onToggleSelect && stageDeals.length > 0 && (
                      <Checkbox
                        checked={stageDeals.length > 0 && stageDeals.every(deal => selectedDealIds.includes(deal.id))}
                        onCheckedChange={() => handleSelectAllInColumn(stageId)}
                        className="flex-shrink-0"
                      />
                    )}
                    <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100 truncate">{t(`stages.${stageId}`)}</h3>
                  </div>
                  <span className={`${config.countBg} ${config.countText} px-2.5 py-0.5 rounded-full text-xs font-medium flex-shrink-0`}>
                    {realCount.toLocaleString()}
                  </span>
                </div>
                
                {/* Linha 2: Valor monetário */}
                {realAmount > 0 && (
                  <div className="flex justify-end">
                    <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">
                      {formatCurrency(realAmount, false)}
                    </span>
                  </div>
                )}
              </div>

              {/* Column Content with Scroll + Hidden Scrollbar */}
              <Droppable droppableId={stageId}>
                {(provided, snapshot) => (
                  <div
                    ref={(el) => {
                      provided.innerRef(el);
                      columnRefs.current[stageId] = el;
                    }}
                    {...provided.droppableProps}
                    className={`${config.bgColor} ${config.borderColor} border-x border-b rounded-b-lg flex-1 overflow-y-auto scrollbar-hide space-y-3 p-3 transition-colors ${
                      snapshot.isDraggingOver ? "border-2 border-blue-400" : ""
                    }`}
                    style={{ maxHeight: 'calc(100dvh - 220px - env(safe-area-inset-bottom, 0px))' }}
                  >
                    {stageDeals.map((deal, index) => (
                      <DealCard
                        key={deal.id}
                        deal={deal}
                        index={index}
                        getContactName={getContactName}
                        getContactPhone={getContactPhone}
                        getAssignedUserName={getAssignedUserName}
                        onViewDeal={onViewDeal}
                        isSelected={selectedDealIds.includes(deal.id)}
                        onToggleSelect={onToggleSelect}
                        scheduledCall={scheduledCallsByDeal?.get(deal.id)}
                        taskCount={taskCountsMap[deal.id]?.count || 0}
                        taskTitles={taskCountsMap[deal.id]?.titles || []}
                        t={t}
                        dateLocale={dateLocale}
                      />
                    ))}
                    {provided.placeholder}
                    
                    {/* Invisible sentinel for infinite scroll trigger */}
                    {hasMore && (
                      <div
                        ref={(el) => scrollSentinelsRef.current[stageId] = el}
                        className="h-8 flex items-center justify-center"
                      >
                        {loadingMoreStage === stageId && (
                          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                        )}
                      </div>
                    )}
                    
                    {/* Loaded indicator */}
                    {stageDeals.length > 0 && stageDeals.length < realCount && !hasMore && (
                      <div className="text-center text-xs text-muted-foreground py-2">
                        {stageDeals.length.toLocaleString()} / {realCount.toLocaleString()}
                      </div>
                    )}
                    
                    {/* Empty State */}
                    {stageDeals.length === 0 && (
                      <div className="flex items-center justify-center h-32 text-slate-400 dark:text-slate-500 text-sm">
                        {t('deals.noDealsYet')}
                      </div>
                    )}
                  </div>
                )}
              </Droppable>
            </div>
          );
        })}
      </div>
      
      {/* Stage Transition Modals */}
      <LostReasonModal
        isOpen={lostReasonModal.isOpen}
        onClose={() => setLostReasonModal({ isOpen: false, dealId: null, deal: null })}
        onConfirm={handleConfirmLostReason}
        isLoading={isProcessingTransition}
      />

      <PostMeetingModal
        isOpen={postMeetingModal.isOpen}
        onClose={() => setPostMeetingModal({ isOpen: false, dealId: null, deal: null })}
        onConfirm={handleConfirmPostMeeting}
        dealId={postMeetingModal.dealId}
        isLoading={isProcessingTransition}
      />

      <QualificationRequiredModal
        isOpen={qualificationModal.isOpen}
        onClose={() => setQualificationModal({ isOpen: false, dealId: null, contactId: null, deal: null, missingFields: [] })}
        onConfirm={handleConfirmQualification}
        dealId={qualificationModal.dealId}
        contactId={qualificationModal.contactId}
        missingFields={qualificationModal.missingFields}
        isLoading={isProcessingTransition}
      />
    </DragDropContext>
  );
}
